import React, { useCallback, useState } from "react";
import { Download } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import { downloadCanvasReport } from "../../lib/reportPdfApi";

/**
 * Drop-in "Download Report" button for the Reports canvas.
 *
 * Usage from the canvas (which owns the placed-widget state):
 *   <DownloadReportButton
 *     widgets={placedWidgets}
 *     meta={{ title, subtitle, dateRange }}
 *     onError={(e) => toast(e.message)}
 *   />
 *
 * `widgets` must be the ordered list of widgets on the main canvas, each
 * carrying the data it already rendered (the snapshot).
 */
export default function DownloadReportButton({
  widgets,
  meta,
  disabled,
  className,
  children,
  onError,
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = useCallback(async () => {
    if (downloading) return;
    const list = Array.isArray(widgets) ? widgets : [];
    if (!list.length) {
      if (onError) onError(new Error("Add at least one widget before downloading the report."));
      return;
    }
    setDownloading(true);
    try {
      await downloadCanvasReport(list, meta || {});
    } catch (err) {
      console.error("Report download failed:", err);
      if (onError) onError(err);
    } finally {
      setDownloading(false);
    }
  }, [widgets, meta, downloading, onError]);

  return (
    <Button
      type="button"
      className={cn("relative bg-violet-600 hover:bg-violet-700", className)}
      disabled={disabled || downloading}
      aria-busy={downloading}
      onClick={handleClick}
    >
      <span className={cn("inline-flex items-center justify-center", downloading && "invisible")}>
        <Download className="mr-1.5 h-4 w-4" aria-hidden />
        {children || "Download Report"}
      </span>
      {downloading ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <LoadingSpinner />
        </span>
      ) : null}
    </Button>
  );
}
